import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format, isValid } from "date-fns";
import { toast } from "react-hot-toast";
import AppSidebar, { MobileTopBar } from "@/components/AppSidebar";
import {
  Moon, Plus, Trash2, PlusCircle, Save, Globe, GlobeLock, Pencil, ArrowLeft, CalendarIcon,
  Sparkles, Loader2, Eye, Presentation, Download, X, Upload,
} from "lucide-react";
import { wednesdayNavItems } from "@/pages/WednesdayNights";
import { GRUDEM_PARTS, getChapterInfo } from "@/lib/grudem";

const emptySession = () => ({
  week_number: 1,
  session_date: "",
  chapter_number: null,
  topic: "",
  description: "",
  youtube_url: "",
  notes: "",
  discussion_questions: [""],
});

const Input = ({ label, value, onChange, placeholder, type = "text", className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    {label && <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>}
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 text-slate-800"
    />
  </div>
);

// Best-effort parse of our own "Wednesday, June 10th, 2026" style strings (strips ordinal suffixes)
// so the calendar can pre-select the currently saved date when editing an existing session.
const parseSessionDate = (str) => {
  if (!str) return undefined;
  const d = new Date(str.replace(/(\d+)(st|nd|rd|th)/, "$1"));
  return isValid(d) ? d : undefined;
};

export default function WednesdayAdmin({ user }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadingSlides, setUploadingSlides] = useState(false);
  const fileInputRef = useRef(null);
  const slidesInputRef = useRef(null);
  const [responsesSessionId, setResponsesSessionId] = useState(null);
  const [responsesData, setResponsesData] = useState(null);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const navItems = wednesdayNavItems(navigate, user, "admin");

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wednesday`);
      setSessions(res.data);
    } catch {
      toast.error("Failed to load sessions");
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "teacher") {
      navigate("/wednesday");
      return;
    }
    loadSessions();
  }, [user, navigate, loadSessions]);

  const startNew = () => {
    const maxWeek = sessions.reduce((m, s) => Math.max(m, s.week_number || 0), 0);
    setEditing({ ...emptySession(), week_number: maxWeek + 1 });
  };

  const handleClassNotesUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".txt")) {
      toast.error("Please select a .txt file");
      return;
    }
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/wednesday/extract-notes`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const maxWeek = sessions.reduce((m, s) => Math.max(m, s.week_number || 0), 0);
      const questions = res.data.discussion_questions?.length ? res.data.discussion_questions : [""];
      setEditing({
        ...emptySession(),
        week_number: maxWeek + 1,
        notes: res.data.notes || "",
        discussion_questions: questions,
      });
      toast.success("Notes and discussion questions organized — review below before saving");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not read that notes file");
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };


  const handleSlidesUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pptx")) {
      toast.error("Please select a .pptx file");
      return;
    }
    if (!editing.session_id) {
      toast.error("Save the session first, then upload slides");
      return;
    }
    setUploadingSlides(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/wednesday/${editing.session_id}/slides`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setEditing((p) => ({
        ...p,
        slides_file_id: res.data.slides_file_id,
        slides_filename: res.data.slides_filename,
      }));
      await loadSessions();
      toast.success("Slides uploaded and converted to PDF");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not convert that file to PDF");
    } finally {
      setUploadingSlides(false);
      if (slidesInputRef.current) slidesInputRef.current.value = "";
    }
  };

  const removeSlides = async () => {
    if (!editing.session_id || !window.confirm("Remove slides from this session?")) return;
    try {
      await axios.delete(`${API}/wednesday/${editing.session_id}/slides`);
      setEditing((p) => ({ ...p, slides_file_id: null, slides_filename: null }));
      await loadSessions();
      toast.success("Slides removed");
    } catch {
      toast.error("Failed to remove slides");
    }
  };

  const startEdit = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/wednesday/${sessionId}`);
      setEditing({
        ...res.data,
        discussion_questions: res.data.discussion_questions?.length ? res.data.discussion_questions : [""],
      });
    } catch {
      toast.error("Failed to load session");
    }
  };

  const saveSession = async () => {
    if (!editing.topic || !editing.week_number) {
      toast.error("Week number and topic are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        week_number: parseInt(editing.week_number, 10) || 1,
        chapter_number: editing.chapter_number ? parseInt(editing.chapter_number, 10) : null,
        discussion_questions: (editing.discussion_questions || []).filter((q) => q.trim()),
      };
      if (editing.session_id) {
        await axios.put(`${API}/wednesday/${editing.session_id}`, payload);
        toast.success("Session updated");
        await loadSessions();
        setEditing(null);
      } else {
        const res = await axios.post(`${API}/wednesday`, payload);
        toast.success("Session created — you can now attach slides below");
        await loadSessions();
        setEditing({
          ...res.data,
          discussion_questions: res.data.discussion_questions?.length ? res.data.discussion_questions : [""],
        });
      }
    } catch {
      toast.error("Failed to save session");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (sessionId) => {
    try {
      await axios.post(`${API}/wednesday/${sessionId}/publish`);
      await loadSessions();
      toast.success("Published status updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm("Delete this session? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/wednesday/${sessionId}`);
      await loadSessions();
      toast.success("Session deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const viewResponses = async (sessionId) => {
    setResponsesSessionId(sessionId);
    setLoadingResponses(true);
    setResponsesData(null);
    try {
      const res = await axios.get(`${API}/wednesday/${sessionId}/all-responses`);
      setResponsesData(res.data);
    } catch {
      toast.error("Failed to load student responses");
      setResponsesSessionId(null);
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleChapterSelect = (val) => {
    const n = val ? parseInt(val, 10) : null;
    setEditing((p) => {
      const info = n ? getChapterInfo(n) : null;
      return {
        ...p,
        chapter_number: n,
        topic: p.topic || (info ? info.title : ""),
      };
    });
  };

  const updateQuestion = (i, val) => {
    setEditing((p) => ({
      ...p,
      discussion_questions: p.discussion_questions.map((q, qi) => (qi === i ? val : q)),
    }));
  };

  if (user && user.role !== "teacher") return null;

  // ── Session list view ──
  if (!editing) {
    return (
      <>
      <div className="min-h-screen bg-slate-50">
        <AppSidebar user={user} active="wednesday" brandLabel="Wednesday Nights" navItems={navItems} />
        <MobileTopBar brandLabel="Wednesday Nights" />
        <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
          <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Manage Sessions</h1>
                <p className="text-sm text-slate-500 mt-0.5">Create and publish Wednesday night teaching sessions.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,text/plain"
                  className="hidden"
                  data-testid="class-notes-txt-file-input"
                  onChange={(e) => handleClassNotesUpload(e.target.files?.[0])}
                />
                <Button
                  data-testid="upload-class-notes-btn"
                  variant="outline"
                  disabled={extracting}
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  {extracting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading Notes...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Upload Class Notes & Auto-Fill</>
                  )}
                </Button>
                <Button
                  data-testid="new-session-btn"
                  onClick={startNew}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Session
                </Button>
              </div>
            </div>

            {sessions.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
                <Moon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No sessions yet</p>
                <p className="text-slate-400 text-sm mt-1">Create your first Wednesday night session to get started.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-session-list">
                {sessions.map((s) => {
                  const ch = s.chapter_number ? getChapterInfo(s.chapter_number) : null;
                  return (
                    <div key={s.session_id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            Week {s.week_number} — {s.topic}
                          </p>
                          <Badge className={s.is_published
                            ? "bg-emerald-100 text-emerald-700 border-emerald-200 text-xs"
                            : "bg-slate-100 text-slate-500 border-slate-200 text-xs"}>
                            {s.is_published ? "Published" : "Draft"}
                          </Badge>
                        </div>
                        {ch && <p className="text-xs text-indigo-600 mt-0.5">Chapter {ch.n}: {ch.title}</p>}
                        {s.session_date && <p className="text-xs text-slate-400 mt-0.5">{s.session_date}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {user?.is_super_admin && (
                          <button
                            data-testid={`view-responses-${s.session_id}`}
                            onClick={() => viewResponses(s.session_id)}
                            title="Student Responses (admin only)"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-sky-600 hover:bg-sky-50 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={`toggle-publish-${s.session_id}`}
                          onClick={() => togglePublish(s.session_id)}
                          title={s.is_published ? "Unpublish" : "Publish"}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                        >
                          {s.is_published ? <GlobeLock className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                        </button>
                        <button
                          data-testid={`edit-session-${s.session_id}`}
                          onClick={() => startEdit(s.session_id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-session-${s.session_id}`}
                          onClick={() => deleteSession(s.session_id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/wednesday?session_id=${s.session_id}`)}
                          className="text-xs rounded-lg"
                        >
                          Preview
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* All Student Responses Dialog (super admin only) */}
      <Dialog open={!!responsesSessionId} onOpenChange={(open) => { if (!open) { setResponsesSessionId(null); setResponsesData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="wednesday-responses-dialog">
          <DialogHeader>
            <DialogTitle>
              {responsesData ? `Student Responses — Week ${responsesData.session.week_number}: ${responsesData.session.topic || ""}` : "Student Responses"}
            </DialogTitle>
          </DialogHeader>
          {loadingResponses ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : responsesData ? (
            responsesData.questions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No discussion questions on this session yet.</p>
            ) : (
              <div className="space-y-4">
                {responsesData.questions.map(q => (
                  <div key={q.index} className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">
                      <span className="font-bold text-indigo-500 mr-1">{q.index + 1}.</span> {q.question}
                    </p>
                    {q.answers.length === 0 ? (
                      <p className="text-xs text-slate-500 italic pl-4">No submitted responses yet.</p>
                    ) : (
                      <div className="space-y-2 pl-4">
                        {q.answers.map(a => (
                          <div key={a.user_id} data-testid={`response-${q.index}-${a.user_id}`} className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                            <p className="text-xs font-semibold text-slate-600 mb-0.5">{a.name}</p>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{a.text || <span className="italic text-slate-400">No response</span>}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : null}
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // ── Edit form ──
  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="wednesday" brandLabel="Wednesday Nights" navItems={navItems} />
      <MobileTopBar brandLabel="Wednesday Nights" />
      <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
          {/* Top bar */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditing(null)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  {editing.session_id ? "Edit Session" : "New Session"}
                </h1>
                {editing.topic && <p className="text-xs text-slate-500">Week {editing.week_number} — {editing.topic}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {editing.session_id && (
                <Button
                  variant="outline"
                  onClick={() => togglePublish(editing.session_id)}
                  size="sm"
                  className="text-xs rounded-lg"
                >
                  {sessions.find((s) => s.session_id === editing.session_id)?.is_published ? (
                    <><GlobeLock className="w-3.5 h-3.5 mr-1.5" /> Unpublish</>
                  ) : (
                    <><Globe className="w-3.5 h-3.5 mr-1.5" /> Publish</>
                  )}
                </Button>
              )}
              <Button
                data-testid="save-session-btn"
                onClick={saveSession}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving…" : "Save Session"}
              </Button>
            </div>
          </div>

          {/* Overview */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Week Number *" type="number" value={editing.week_number}
                onChange={(v) => setEditing((p) => ({ ...p, week_number: v }))} placeholder="1" />
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Session Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="session-date-picker-btn"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal border-slate-200 hover:bg-slate-50",
                        !editing.session_date && "text-slate-400"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {editing.session_date || "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={parseSessionDate(editing.session_date)}
                      defaultMonth={parseSessionDate(editing.session_date) || new Date()}
                      onSelect={(date) =>
                        setEditing((p) => ({
                          ...p,
                          session_date: date ? format(date, "EEEE, MMMM do, yyyy") : "",
                        }))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Chapter</label>
              <select
                data-testid="chapter-select"
                value={editing.chapter_number || ""}
                onChange={(e) => handleChapterSelect(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 text-slate-800 bg-white"
              >
                <option value="">— No chapter (special session) —</option>
                {GRUDEM_PARTS.map((part) => (
                  <optgroup key={part.part} label={`Part ${part.part}: ${part.title}`}>
                    {part.chapters.map((ch) => (
                      <option key={ch.n} value={ch.n}>
                        Ch. {ch.n} — {ch.title}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <Input label="Topic *" value={editing.topic}
              onChange={(v) => setEditing((p) => ({ ...p, topic: v }))}
              placeholder="e.g. The Character of God" />

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Description (optional)</label>
              <textarea
                value={editing.description || ""}
                onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
                placeholder="Short intro shown at the top of the session page…"
                rows={2}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 text-slate-800 resize-none"
              />
            </div>

            <Input label="YouTube Video URL" value={editing.youtube_url || ""}
              onChange={(v) => setEditing((p) => ({ ...p, youtube_url: v }))}
              placeholder="https://www.youtube.com/watch?v=…" />
          </div>

          {/* Slides */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Slides (PPTX)</label>
            <input
              ref={slidesInputRef}
              type="file"
              accept=".pptx"
              className="hidden"
              data-testid="slides-pptx-file-input"
              onChange={(e) => handleSlidesUpload(e.target.files?.[0])}
            />
            {editing.slides_file_id ? (
              <div className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-2.5" data-testid="slides-current-file">
                <div className="flex items-center gap-2 min-w-0">
                  <Presentation className="w-4 h-4 text-indigo-500 shrink-0" />
                  <span className="text-sm text-slate-700 truncate">{editing.slides_filename || "Slides.pdf"}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a
                    href={`${API}/files/${editing.slides_file_id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="slides-download-link"
                    className="p-1.5 rounded-lg text-indigo-500 hover:text-indigo-700 hover:bg-indigo-100 transition-colors"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <button
                    data-testid="slides-replace-btn"
                    onClick={() => slidesInputRef.current?.click()}
                    disabled={uploadingSlides}
                    title="Replace"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-100 transition-colors"
                  >
                    {uploadingSlides ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </button>
                  <button
                    data-testid="slides-remove-btn"
                    onClick={removeSlides}
                    title="Remove"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-100 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <Button
                  data-testid="slides-upload-btn"
                  type="button"
                  variant="outline"
                  disabled={uploadingSlides || !editing.session_id}
                  onClick={() => slidesInputRef.current?.click()}
                  className="rounded-xl border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                >
                  {uploadingSlides ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Converting to PDF…</>
                  ) : (
                    <><Presentation className="w-4 h-4 mr-2" /> Upload Slides (.pptx)</>
                  )}
                </Button>
                <p className="text-xs text-slate-400 mt-1.5">
                  {editing.session_id
                    ? "PPTX is automatically converted to PDF for download and in-browser viewing."
                    : "Save the session first, then come back to attach slides."}
                </p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Teaching Notes</label>
            <p className="text-xs text-slate-400">
              Formatting: <code className="bg-slate-100 px-1 rounded">## Heading</code>,{" "}
              <code className="bg-slate-100 px-1 rounded">- bullet point</code>,{" "}
              <code className="bg-slate-100 px-1 rounded">**bold**</code>. Blank lines separate paragraphs.
            </p>
            <textarea
              data-testid="notes-textarea"
              value={editing.notes || ""}
              onChange={(e) => setEditing((p) => ({ ...p, notes: e.target.value }))}
              placeholder={"## God's Communicable Attributes\n\n- **Love** — God eternally gives of Himself to others\n- **Holiness** — separated from sin and devoted to His own honor"}
              rows={12}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 text-slate-800 font-mono"
            />
          </div>

          {/* Discussion Questions */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Discussion Questions</label>
            {(editing.discussion_questions || []).map((q, i) => (
              <div key={i} className="flex gap-2">
                <input
                  data-testid={`question-input-${i}`}
                  type="text"
                  value={q}
                  onChange={(e) => updateQuestion(i, e.target.value)}
                  placeholder={`Question ${i + 1}…`}
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
                />
                <button
                  onClick={() => setEditing((p) => ({ ...p, discussion_questions: p.discussion_questions.filter((_, qi) => qi !== i) }))}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              data-testid="add-question-btn"
              onClick={() => setEditing((p) => ({ ...p, discussion_questions: [...(p.discussion_questions || []), ""] }))}
              className="text-xs text-indigo-600 font-semibold hover:text-indigo-700 flex items-center gap-1"
            >
              <PlusCircle className="w-3.5 h-3.5" /> Add Question
            </button>
          </div>

          {/* Bottom save bar */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={saveSession}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving…" : "Save Session"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
